import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useLogto } from '@logto/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, useApiClient } from '@/api/client';
import { Sentry } from '@/monitoring/sentry';
import { useAuth } from './useAuth';

export interface User {
  id: string;
  email: string;
  name: string | null;
  avatar_url: string | null;
  locale: string;
  timezone: string;
  is_super_admin: boolean;
}

interface UserContextValue {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  updateUserLocal: (user: User) => void;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading: isLogtoLoading, getIdTokenClaims } = useLogto();
  const { fetchWithAuth } = useApiClient();
  const { logout } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery<User>({
    queryKey: ['current-user'],
    queryFn: async () => {
      const createUserFromClaims = async () => {
        const idTokenClaims = await getIdTokenClaims();
        if (!idTokenClaims?.email) {
          logout();
          throw new Error('No email in ID token claims');
        }

        try {
          return await fetchWithAuth<User>('/api/me', {
            method: 'POST',
            body: JSON.stringify({
              email: idTokenClaims.email,
              name: idTokenClaims.name ?? null,
              avatar_url: idTokenClaims.picture ?? null,
            }),
          });
        } catch (createErr) {
          if (createErr instanceof ApiError && createErr.status === 401) {
            logout();
          }
          throw createErr;
        }
      };

      try {
        return await fetchWithAuth<User>('/api/me');
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.status === 404 && err.code === 'user_not_found') {
            return createUserFromClaims();
          }
          if (err.status === 401) {
            logout();
          }
        }
        throw err;
      }
    },
    enabled: isAuthenticated && !isLogtoLoading,
    staleTime: Infinity,
    retry: 1,
  });

  const refetch = async () => {
    await queryClient.invalidateQueries({ queryKey: ['current-user'] });
  };

  const updateUserLocal = (user: User) => {
    queryClient.setQueryData(['current-user'], user);
  };

  useEffect(() => {
    if (query.data) {
      Sentry.setUser({ id: query.data.id, email: query.data.email });
      return;
    }
    if (!isAuthenticated && !isLogtoLoading) {
      Sentry.setUser(null);
    }
  }, [query.data, isAuthenticated, isLogtoLoading]);

  return (
    <UserContext.Provider value={{
      user: query.data ?? null,
      isLoading: query.isLoading,
      error: query.error instanceof Error ? query.error.message : null,
      refetch,
      updateUserLocal,
    }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error('useUser must be used within UserProvider');
  }
  return ctx;
}
